/**
 * Générateur — Ordonner une liste de nombres décimaux
 * Attendu ID : 605 | Niveau : 6ème | Domaine : Nombres et calculs
 * Cluster 4 — Comparer / Ordonner des décimaux
 *
 * Types générés :
 *   rangerListe_ordonner, rangerListe_qcm_unique, rangerListe_texte,
 *   rangerListe_texte_trou_drag,
 *   etiquettes_ordonner,
 *   tableau_ordonner,
 *   intrus_qcm_unique
 *
 * Labels EXERCICES_REFERENCE.md :
 *   - Ranger une liste dans l'ordre croissant ou décroissant
 *   - Former puis ranger des décimaux à partir d'étiquettes
 *   - Ranger selon une colonne d'un tableau contextuel
 *   - Barrer l'intrus qui rompt l'ordre
 *
 * Variantes estimées : ~300+ combinaisons
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, makeOrdonner,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 605;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Labels ───
const L_RANGER  = "Ranger une liste dans l'ordre croissant ou décroissant";
const L_ETIQ    = "Former puis ranger des décimaux à partir d'étiquettes";
const L_TABLEAU = "Ranger selon une colonne d'un tableau contextuel";
const L_INTRUS  = "Barrer l'intrus qui rompt l'ordre";

// ─── Helpers ───
const parseFr = (s) => parseFloat(String(s).replace(',', '.'));

const sortFr = (arr, order = 'croissant') => {
    const copy = [...arr];
    copy.sort((a, b) => {
        const da = parseFr(a), db = parseFr(b);
        return order === 'croissant' ? da - db : db - da;
    });
    return copy;
};

const allDistinct = (arr) => {
    const vals = arr.map(parseFr);
    for (let i = 0; i < vals.length; i++)
        for (let j = i + 1; j < vals.length; j++)
            if (Math.abs(vals[i] - vals[j]) < 1e-9) return false;
    return true;
};

// ─── Pool de listes à ranger ─────────────────────
// Chaque liste est donnée dans un ordre mélangé (non trié).
// Valeurs choisies pour activer E1 (partie décimale comparée comme entier),
// E2/E3 (zéros), E12 (partie entière vs décimale).
const LISTES_A_RANGER = [
    ['0,9', '0,12', '0,109', '0,2'],            // E1 + E3
    ['1,9', '1,12', '1,09', '1,2'],             // E1
    ['4,8', '4,88', '4,808', '4,08'],           // E1 + E3
    ['7,5', '7,49', '7,52', '7,509'],           // E1
    ['12,3', '12,29', '12,301', '12,31'],       // E1 + E3
    ['0,6', '0,06', '0,66', '0,606', '0,066'],  // E1 + E3
    ['3,14', '3,141', '3,1', '3,104'],          // E1 + E3
    ['8,2', '8,25', '8,02', '8,022', '8,052'],  // E1 + E3
    ['15,7', '15,69', '15,8', '15,702'],        // E1 + E3
    ['0,3', '0,03', '0,303', '0,033'],          // E1 + E3
    ['5,05', '5,5', '5,55', '5,005'],           // E3
    ['9', '9,01', '9,1', '8,99'],               // E12
];

// ─── Pool étiquettes ─────────────────────────────
// Chaque entrée : decimaux déjà formés par l'élève à partir d'étiquettes
// que l'on liste pour l'énoncé (tags).
const ETIQUETTES_POOL = [
    { tags: ['1', '3', '7', ','], decimaux: ['1,73', '7,13', '3,17', '7,31'] },
    { tags: ['0', '2', '5', ','], decimaux: ['0,52', '5,02', '2,05', '2,50'] },
    { tags: ['0', '4', '8', ','], decimaux: ['0,84', '8,04', '4,08', '4,80'] },
    { tags: ['0', '6', '9', ','], decimaux: ['0,69', '6,09', '9,06', '9,60'] },
    { tags: ['1', '2', '5', ','], decimaux: ['1,25', '2,15', '5,12', '5,21'] },
    { tags: ['3', '4', '7', ','], decimaux: ['3,47', '4,73', '7,34', '7,43'] },
];

// ─── Pool contextes tabulaires ───────────────────
const CONTEXTES_TABLEAU = [
    {
        titre: 'Hauteur de sommets alpins',
        colValue: 'Altitude (km)',
        colLabel: 'Sommet',
        question: 'Range les sommets du plus bas au plus haut.',
        ordre: 'croissant',
        lignes: [
            { nom: 'Mont Blanc',       val: '4,810' },
            { nom: 'Cervin',           val: '4,478' },
            { nom: 'Mont Rose',        val: '4,634' },
            { nom: 'Grand Paradis',    val: '4,061' },
            { nom: 'Barre des Écrins', val: '4,102' },
        ],
    },
    {
        titre: 'Temps au 100 m',
        colValue: 'Temps (s)',
        colLabel: 'Coureur',
        question: 'Range les coureurs du plus rapide au plus lent.',
        ordre: 'croissant',
        lignes: [
            { nom: 'Antoine', val: '12,45'  },
            { nom: 'Bruno',   val: '12,4'   },
            { nom: 'Camille', val: '12,405' },
            { nom: 'David',   val: '12,54'  },
            { nom: 'Émile',   val: '12,5'   },
        ],
    },
    {
        titre: 'Masse de colis',
        colValue: 'Masse (kg)',
        colLabel: 'Colis',
        question: 'Range les colis du plus lourd au plus léger.',
        ordre: 'décroissant',
        lignes: [
            { nom: 'A', val: '2,3'   },
            { nom: 'B', val: '2,25'  },
            { nom: 'C', val: '2,305' },
            { nom: 'D', val: '2,5'   },
            { nom: 'E', val: '2,03'  },
        ],
    },
    {
        titre: 'Longueur de fleuves (en milliers de km)',
        colValue: 'Longueur (milliers de km)',
        colLabel: 'Fleuve',
        question: 'Range les fleuves du plus court au plus long.',
        ordre: 'croissant',
        lignes: [
            { nom: 'Loire',    val: '1,006' },
            { nom: 'Rhin',     val: '1,23'  },
            { nom: 'Danube',   val: '2,85'  },
            { nom: 'Volga',    val: '3,53'  },
            { nom: 'Mékong',   val: '4,35'  },
            { nom: 'Amazone',  val: '6,4'   },
        ],
    },
];

// ─── Pool intrus ─────────────────────────────────
// Vérifié : retirer l'intrus → ordre strict respecté ; avec l'intrus → rompu.
const INTRUS_POOL = [
    { liste: ['7,09', '7,1', '21,25', '21,2', '21,22', '27,3'], ordre: 'croissant',   intrus: '21,25' },
    { liste: ['3,2', '3,25', '3,3', '3,35', '3,29', '3,4'],     ordre: 'croissant',   intrus: '3,29'  },
    { liste: ['5,8', '5,79', '5,75', '5,705', '5,702', '5,71', '5,6'], ordre: 'décroissant', intrus: '5,71' },
    { liste: ['12,5', '12,48', '12,4', '12,39', '12,41', '12,3'], ordre: 'décroissant', intrus: '12,41' },
    { liste: ['0,08', '0,1', '0,12', '0,18', '0,09', '0,2'],    ordre: 'croissant',   intrus: '0,09'  },
    { liste: ['9', '9,1', '9,09', '9,12', '9,2', '9,3'],        ordre: 'croissant',   intrus: '9,09'  },
    { liste: ['8,9', '8,88', '8,8', '8,808', '8,79', '8,8'],    ordre: 'décroissant', intrus: null    }, // fallback : ignoré, on en a assez
];

// ────────────────────────────────────────────────────────────────
// rangerListe
// ────────────────────────────────────────────────────────────────

/**
 * Génère dynamiquement une liste de N décimaux avec même partie entière.
 * Garantit un mix de longueurs décimales (1 digit ET 2-3 digits) pour activer
 * naturellement le piège E1 (partie décimale comparée comme entier).
 * @param {Object} [opts]
 * @param {number} [opts.count] — nombre d'éléments (défaut aléatoire 4-5)
 */
const genListeDynamique = (opts = {}) => {
    const intPart = randInt(0, 15);
    const count = opts.count || randInt(4, 5);
    const set = new Set();
    // Forcer au moins 1 décimale courte + 1 longue pour garantir un piège E1
    const baseLens = [1, randInt(2, 3), 1, randInt(2, 3), 1, randInt(2, 3), randInt(1, 3)];
    const decLens = baseLens.slice(0, count);
    let guard = 0;
    while (set.size < count && guard < 100) {
        const decLen = decLens[set.size] || randInt(1, 3);
        const decVal = randInt(0, Math.pow(10, decLen) - 1);
        const decStr = decVal.toString().padStart(decLen, '0');
        set.add(`${intPart},${decStr}`);
        guard++;
    }
    const arr = [...set];
    return allDistinct(arr) && arr.length >= Math.min(count, 4) ? arr : null;
};

/**
 * Génère dynamiquement une liste-intrus : liste de 6-7 décimaux qui serait triée
 * si on retirait l'intrus, mais qui est rompue dans l'ordre avec l'intrus.
 * Retourne { liste, ordre, intrus } ou null si échec.
 */
const genIntrusDynamique = () => {
    const ordre = Math.random() < 0.5 ? 'croissant' : 'décroissant';
    let base = null;
    for (let i = 0; i < 10 && !base; i++) {
        base = genListeDynamique({ count: randInt(6, 7) });
    }
    if (!base || base.length < 5) return null;
    const trie = sortFr(base, ordre);
    // Choisir un élément à déplacer (pas aux bords pour garantir mouvement possible)
    const n = trie.length;
    const iOrigin = randInt(1, n - 2);
    const intrus = trie[iOrigin];
    const without = [...trie.slice(0, iOrigin), ...trie.slice(iOrigin + 1)];
    // Replacer l'intrus à une position qui casse l'ordre
    for (let attempts = 0; attempts < 20; attempts++) {
        const jNew = randInt(0, without.length);
        if (jNew === iOrigin) continue;
        const newListe = [...without.slice(0, jNew), intrus, ...without.slice(jNew)];
        const sortedAgain = sortFr(newListe, ordre);
        if (JSON.stringify(newListe) !== JSON.stringify(sortedAgain)) {
            return { liste: newListe, ordre, intrus };
        }
    }
    return null;
};

const randListeEtOrdre = () => {
    // 50% pool curé (pièges fins tuned), 50% dynamique (variété infinie + E1 garanti)
    let liste;
    if (Math.random() < 0.5) {
        liste = randomChoice(LISTES_A_RANGER);
    } else {
        liste = genListeDynamique() || randomChoice(LISTES_A_RANGER);
    }
    const ordre = Math.random() < 0.5 ? 'croissant' : 'décroissant';
    const sorted = sortFr(liste, ordre);
    return { liste, ordre, sorted };
};

const genRangerOrdonner = () => {
    const { liste, ordre, sorted } = randListeEtOrdre();
    const texte = `<p>Range ces nombres dans l'ordre <strong>${ordre}</strong> : <strong>${liste.join(' ; ')}</strong></p>`;
    return makeOrdonner(sorted, texte, opts(L_RANGER));
};

const genRangerQcmUnique = () => {
    const { liste, ordre, sorted } = randListeEtOrdre();
    const sep = ordre === 'croissant' ? ' < ' : ' > ';
    const correct = sorted.join(sep);

    // Distracteurs : chacun contient une erreur classique
    const distractors = new Set();
    // D1 : l'ordre inverse (piège E7)
    distractors.add([...sorted].reverse().join(sep));
    // D2 : tri par longueur de la partie décimale (piège E1 générique)
    const parDec = [...liste].sort((a, b) => {
        const la = (a.split(',')[1] || '').length;
        const lb = (b.split(',')[1] || '').length;
        return ordre === 'croissant' ? la - lb : lb - la;
    });
    distractors.add(parDec.join(sep));
    // D3 : tri comme si les parties décimales étaient des entiers (E1 pur)
    const commeEntiers = [...liste].sort((a, b) => {
        const [ea, da = ''] = a.split(',');
        const [eb, db = ''] = b.split(',');
        if (parseInt(ea) !== parseInt(eb)) {
            return ordre === 'croissant'
                ? parseInt(ea) - parseInt(eb)
                : parseInt(eb) - parseInt(ea);
        }
        return ordre === 'croissant'
            ? parseInt(da || '0') - parseInt(db || '0')
            : parseInt(db || '0') - parseInt(da || '0');
    });
    distractors.add(commeEntiers.join(sep));

    distractors.delete(correct);
    const distArr = [...distractors].slice(0, 3);
    // Fallback si < 3 distracteurs uniques
    while (distArr.length < 3) {
        const alt = shuffle(liste).join(sep);
        if (alt !== correct && !distArr.includes(alt)) distArr.push(alt);
    }

    const choix = [correct, ...distArr];
    const texte = `<p>Parmi les propositions ci-dessous, laquelle range correctement les nombres <strong>${liste.join(' ; ')}</strong> dans l'ordre <strong>${ordre}</strong> ?</p>`;
    return makeQcm(choix, correct, texte, opts(L_RANGER));
};

const genRangerTexte = () => {
    const { liste, ordre, sorted } = randListeEtOrdre();
    const reponse = sorted.join(' ; ');
    const texte = `<p>Range ces nombres dans l'ordre <strong>${ordre}</strong> en les séparant par <code> ; </code> (espace, point-virgule, espace) : <strong>${liste.join(' ; ')}</strong></p>`;
    return makeSaisie(reponse, texte, { ...opts(L_RANGER), strict: true });
};

const genRangerDrag = () => {
    const { liste, ordre, sorted } = randListeEtOrdre();
    const sep = ordre === 'croissant' ? ' < ' : ' > ';
    // Chaque valeur devient un trou
    const phrase = sorted.map(v => `{{${v}}}`).join(sep);
    const texte = `<p>Glisse les nombres pour les ranger dans l'ordre <strong>${ordre}</strong>.</p>`;
    return makeTexteTrou(phrase, texte, {
        ...opts(L_RANGER),
        mode: 'drag',
        distracteurs: [],
        strict: true,
    });
};

// ────────────────────────────────────────────────────────────────
// etiquettes
// ────────────────────────────────────────────────────────────────

const genEtiquettesOrdonner = () => {
    const e = randomChoice(ETIQUETTES_POOL);
    const ordre = Math.random() < 0.5 ? 'croissant' : 'décroissant';
    const sorted = sortFr(e.decimaux, ordre);
    // On mélange l'affichage pour éviter qu'il soit déjà trié par hasard.
    let displayed = shuffle(e.decimaux);
    for (let tries = 0; tries < 10; tries++) {
        const isAsc  = displayed.every((v, i) => i === 0 || parseFr(displayed[i-1]) <  parseFr(v));
        const isDesc = displayed.every((v, i) => i === 0 || parseFr(displayed[i-1]) >  parseFr(v));
        if (!isAsc && !isDesc) break;
        displayed = shuffle(e.decimaux);
    }
    // Chaque étiquette dans un vrai carré (inline-style — Tailwind JIT ne détecte
    // pas les classes dans les strings construits dynamiquement).
    const tagStyle = 'display:inline-block;min-width:28px;padding:4px 10px;margin:0 4px;border:2px solid #94a3b8;border-radius:6px;background:#334155;color:#e2e8f0;text-align:center;font-family:monospace;font-weight:bold;font-size:16px';
    const tagsStr = e.tags.map(t => `<span style="${tagStyle}">${t}</span>`).join('');
    const texte =
        `<p>À partir des étiquettes ${tagsStr}, on a formé les nombres décimaux suivants :</p>` +
        `<p><strong>${displayed.join(' ; ')}</strong></p>` +
        `<p>Range-les dans l'ordre <strong>${ordre}</strong>.</p>`;
    return makeOrdonner(sorted, texte, opts(L_ETIQ));
};

// ────────────────────────────────────────────────────────────────
// tableau contextuel
// ────────────────────────────────────────────────────────────────

const genTableauOrdonner = () => {
    const ctx = randomChoice(CONTEXTES_TABLEAU);
    // Construire l'ordre correct des NOMS selon la colonne valeur
    const withVals = ctx.lignes.map(l => ({ nom: l.nom, num: parseFr(l.val) }));
    withVals.sort((a, b) =>
        ctx.ordre === 'croissant' ? a.num - b.num : b.num - a.num
    );
    const sortedNoms = withVals.map(x => x.nom);

    // Construire un tableau HTML inline dans l'énoncé
    const rowsShuffled = shuffle(ctx.lignes);
    const thead = `<thead><tr><th>${ctx.colLabel}</th><th>${ctx.colValue}</th></tr></thead>`;
    const tbody = '<tbody>' + rowsShuffled.map(l =>
        `<tr><td>${l.nom}</td><td>${l.val}</td></tr>`
    ).join('') + '</tbody>';
    const table = `<table>${thead}${tbody}</table>`;

    const texte =
        `<p><strong>${ctx.titre}</strong></p>` +
        table +
        `<p>${ctx.question}</p>`;

    return makeOrdonner(sortedNoms, texte, opts(L_TABLEAU));
};

// ────────────────────────────────────────────────────────────────
// intrus
// ────────────────────────────────────────────────────────────────

const genIntrusQcmUnique = () => {
    // 50% dynamique (variété infinie, piège E1 garanti) / 50% pool curé
    let e;
    if (Math.random() < 0.5) {
        e = genIntrusDynamique();
    }
    if (!e) {
        const valides = INTRUS_POOL.filter(p => p.intrus !== null);
        e = randomChoice(valides);
    }
    const texte =
        `<p>La liste suivante devait être rangée dans l'ordre <strong>${e.ordre}</strong>, mais un nombre s'est glissé à la mauvaise place. Lequel est l'intrus ?</p>` +
        `<p><strong>${e.liste.join(' ; ')}</strong></p>`;
    return makeQcm(e.liste, e.intrus, texte, opts(L_INTRUS));
};

// ────────────────────────────────────────────────────────────────
// Registre
// ────────────────────────────────────────────────────────────────

const GENERATORS = {
    rangerListe_ordonner:         genRangerOrdonner,
    rangerListe_qcm_unique:       genRangerQcmUnique,
    rangerListe_texte:            genRangerTexte,
    rangerListe_texte_trou_drag:  genRangerDrag,
    etiquettes_ordonner:          genEtiquettesOrdonner,
    tableau_ordonner:             genTableauOrdonner,
    intrus_qcm_unique:            genIntrusQcmUnique,
};

// ────────────────────────────────────────────────────────────────
// CATEGORIES (affichage des cards)
// ────────────────────────────────────────────────────────────────

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'rangerListe', label: L_RANGER,  formatsList: buildFormats('rangerListe') },
    { id: 'etiquettes',  label: L_ETIQ,    formatsList: buildFormats('etiquettes')  },
    { id: 'tableau',     label: L_TABLEAU, formatsList: buildFormats('tableau')     },
    { id: 'intrus',      label: L_INTRUS,  formatsList: buildFormats('intrus')      },
];

// ────────────────────────────────────────────────────────────────
// Point d'entrée
// ────────────────────────────────────────────────────────────────

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            k => k.startsWith(typeExercice) || k.includes(typeExercice)
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
